import * as React from 'react';
import { Link } from 'gatsby';
import type { storyblokEditable } from '@storyblok/react';

import type { StoryblokMultilink } from '../../types/storyblok';

export interface MultilinkLinkProps {
  to: StoryblokMultilink | undefined;
  children: React.ReactNode;
  className?: string;
  externalLabel?: string;
  /**
   * Optional editor-wiring attributes produced by `@storyblok/react`'s
   * `storyblokEditable(blok)`. When provided, they are spread onto the rendered
   * anchor root of every branch so Storyblok's bridge can highlight the parent
   * blok for click-to-edit. Existing callers that omit this prop retain
   * identical behavior because spreading `undefined` is a no-op.
   */
  editableAttrs?: ReturnType<typeof storyblokEditable>;
}

const normalizeStoryHref = (cachedUrl: string): string => `/${cachedUrl.replace(/^\/+/, '')}`;

export const MultilinkLink: React.FC<MultilinkLinkProps> = ({
  to,
  children,
  className,
  externalLabel = 'opens in new tab',
  editableAttrs,
}) => {
  if (!to) {
    console.warn('MultilinkLink: received an undefined target; rendering nothing.');
    return null;
  }

  if (to.linktype === 'story') {
    // Editor-authored target="_blank" overrides the default Gatsby client-side
    // navigation: render a plain <a target="_blank"> so the destination opens
    // in a new tab even for internal links (per Requirement 3.5).
    if (to.target === '_blank') {
      return (
        <a
          href={normalizeStoryHref(to.cached_url)}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          {...editableAttrs}
        >
          {children}
        </a>
      );
    }
    return (
      <Link to={normalizeStoryHref(to.cached_url)} className={className} {...editableAttrs}>
        {children}
      </Link>
    );
  }

  if (to.linktype === 'url') {
    return (
      <a
        href={to.url || to.cached_url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        {...editableAttrs}
      >
        {children}
        <span className="sr-only"> ({externalLabel})</span>
      </a>
    );
  }

  if (to.linktype === 'email') {
    return (
      <a href={`mailto:${to.email}`} className={className} {...editableAttrs}>
        {children}
      </a>
    );
  }

  if (to.linktype === 'asset') {
    return (
      <a
        href={to.url || to.cached_url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        {...editableAttrs}
      >
        {children}
      </a>
    );
  }

  // Exhaustiveness guard: all four `linktype` discriminants are handled above,
  // so `to` is narrowed to `never` here. The cast keeps a defensive runtime
  // warning in case the upstream payload drifts past the typed union.
  const exhaustiveCheck: never = to;
  console.warn(
    `MultilinkLink: linktype "${(exhaustiveCheck as StoryblokMultilink).linktype}" is not supported; rendering nothing.`
  );
  return null;
};
